import { useCallback } from 'react';

import { VectilesMetricGroups } from '@hcs/types';

import { VECTILES_METRICS_GROUP_CONFIG } from '../constants';
import {
  HcMapChangeVectileGroupArgs,
  HcMapChangeVectileMetricArgs,
  HcMapDisableMarkerClustersArgs,
  HcMapMountArgs,
  HcMapShowMonochromeArgs,
} from '../rtk';

import { useHcMapPreferences } from './useHcMapPreferences';
import { useHcMapSlice } from './useHcMapSlice';

export const useHcMap = (mapId: string) => {
  const hcMapSlice = useHcMapSlice();
  const actions = hcMapSlice.actions;
  const {
    hcMapMount,
    hcMapLayersShowMonochrome,
    hcMapChangeVectilesMetricGroup,
    hcMapDisableMarkerClusters,
  } = actions;
  const mapState = hcMapSlice.state[mapId];
  const {
    state: mapPreferences,
    actions: {
      setHeatmapLayerPreference,
      setHeatmapsMonochromePreference,
      setDisableMarkerClusters,
    },
  } = useHcMapPreferences(mapId);

  return {
    mapState,
    actions: {
      ...actions,
      // Combine redux slice w/ localStorage preferences
      hcMapMount: useCallback(
        ({ mapId, mapState }: HcMapMountArgs) => {
          return hcMapMount({
            mapId,
            mapState: {
              ...mapState,
              markers: {
                ...mapState.markers,
                ...mapPreferences?.markers,
              },
              heatmap: {
                ...mapState.heatmap,
                ...mapPreferences?.heatmap,
              },
            },
          });
        },
        [mapPreferences, hcMapMount],
      ),
      // Methods below here keep the localStorage map preferences in sync w/ the updates to map state
      hcMapLayersShowMonochrome: useCallback(
        (payload: HcMapShowMonochromeArgs) => {
          setHeatmapsMonochromePreference(payload);
          return hcMapLayersShowMonochrome(payload);
        },
        [setHeatmapsMonochromePreference, hcMapLayersShowMonochrome],
      ),
      hcMapChangeVectilesMetricGroup: useCallback(
        (payload: {
          mapId: string;
          vectilesMetricGroup: VectilesMetricGroups | null;
        }) => {
          // Use the default metric ID for the new group
          const vectilesMetricId = payload.vectilesMetricGroup
            ? VECTILES_METRICS_GROUP_CONFIG[payload.vectilesMetricGroup]
                .vectilesMetricIds[0]
            : null;
          // New payload includes all info the slice needs to update state
          const rtkPayload: HcMapChangeVectileGroupArgs =
            payload.vectilesMetricGroup && vectilesMetricId
              ? {
                  vectilesMetricGroup: payload.vectilesMetricGroup,
                  vectilesMetricId,
                  mapId: payload.mapId,
                }
              : {
                  vectilesMetricGroup: null,
                  mapId: payload.mapId,
                };
          setHeatmapLayerPreference(rtkPayload);
          return hcMapChangeVectilesMetricGroup(rtkPayload);
        },
        [hcMapChangeVectilesMetricGroup, setHeatmapLayerPreference],
      ),
      hcMapChangeVectilesMetric: useCallback(
        (payload: HcMapChangeVectileMetricArgs) => {
          const { vectilesMetricId } = payload;
          if (vectilesMetricId && mapState?.heatmap.vectilesMetricGroup) {
            setHeatmapLayerPreference({
              vectilesMetricGroup: mapState.heatmap.vectilesMetricGroup,
              vectilesMetricId,
              mapId,
            });
          } else {
            setHeatmapLayerPreference({
              vectilesMetricGroup: null,
              mapId,
            });
          }
          return actions.hcMapChangeVectilesMetric(payload);
        },
        [],
      ),
      hcMapDisableMarkerClusters: useCallback(
        (payload: HcMapDisableMarkerClustersArgs) => {
          setDisableMarkerClusters(payload);
          return hcMapDisableMarkerClusters(payload);
        },
        [hcMapDisableMarkerClusters, setDisableMarkerClusters],
      ),
    },
  };
};
